import { FC, useState } from 'react';
import classNames from 'classnames';
import { SpocState } from '../../store/authentication/state';
import { LoanFormatIcon } from '../../shared/common/GildIcon';
import { useTranslation } from 'react-i18next';
import FormatCurrency from '../../shared/utils/formatCurrency';
import { DropDownMenu } from '../../shared/common/DropDownMenu';

type Props = {
    readonly spoc?: SpocState;
    readonly textTotalLine?: string;
    readonly totalNetValue?: number;
    readonly totalTaxValue?: number;
    readonly totalValue?: number;
    readonly currency?: string;
    readonly ebooksAmountVisible?: boolean;
    readonly ebooksAmount?: number;
    readonly eaudiobooksAmountVisible?: boolean;
    readonly eaudiobooksAmount?: number;
    readonly epressAmountVisible?: boolean;
    readonly epressAmount?: number;
    readonly className?: string;
};

const TotalDropDown: FC<Props> = props => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const { spoc, totalNetValue, currency, className, textTotalLine } = props;

    const classes = classNames('default-dd-nav', 'expanded-dd', 'total-list', 'dd-a-r', className);

    const label = (
        <>
            {`${textTotalLine} ${t('numberFormat.numberWithCurrencySymbol', {
                val: FormatCurrency.convertIntegerToCurrencyFormat(totalNetValue),
                currencySymbol: FormatCurrency.mapCurrencyToSymbol(currency)
            })}`}
            <span className="small"> {t('components.totalDropdown.exTax', { tax: spoc?.taxName ?? 'tax' })}</span>
        </>
    );
    return (
        <DropDownMenu
            className={classes}
            arrowClassName="md-up"
            label={label}
            expanded={expanded}
            onToggleClick={() => setExpanded(!expanded)}
            align="right"
            gap={10}
            onClickOutside={() => setExpanded(false)}
        >
            <TotalTable {...props} />
        </DropDownMenu>
    );
};

export default TotalDropDown;

export const TotalTable: FC<Props> = ({
    spoc,
    ebooksAmountVisible = true,
    ebooksAmount,
    eaudiobooksAmountVisible = true,
    eaudiobooksAmount,
    epressAmountVisible = true,
    epressAmount,
    textTotalLine,
    totalNetValue,
    totalTaxValue,
    totalValue,
    currency
}) => {
    const { t } = useTranslation();

    return (
        <table className="total small" data-testid="total-table">
            <thead>
                <tr>
                    <th colSpan={2}>
                        <strong>{t('components.totalDropdown.totalQuantities')}</strong>
                    </th>
                </tr>
            </thead>
            <tbody>
                {ebooksAmountVisible ? (
                    <tr>
                        <td>
                            <div className="display-flex">
                                <LoanFormatIcon icon="ebook-filled" className="loanformat-icon-table" />
                                <span>{t('loanFormat.plain.ebooks', { count: 10 })}</span>
                            </div>
                        </td>
                        <td className="a-right">{ebooksAmount}</td>
                    </tr>
                ) : null}
                {eaudiobooksAmountVisible ? (
                    <tr>
                        <td>
                            <div className="display-flex">
                                <LoanFormatIcon icon="eaudiobook-filled" className="loanformat-icon-table" />
                                <span>{t('loanFormat.plain.eaudiobooks', { count: 10 })}</span>
                            </div>
                        </td>
                        <td className="a-right">{eaudiobooksAmount}</td>
                    </tr>
                ) : null}
                {epressAmountVisible ? (
                    <tr>
                        <td>
                            <div className="display-flex">
                                <LoanFormatIcon icon="emagazine-filled" className="loanformat-icon-table" />
                                <span>{t('loanFormat.plain.epress', { count: 10 })}</span>
                            </div>
                        </td>
                        <td className="a-right">{epressAmount ?? 0}</td>
                    </tr>
                ) : null}
                <tr className="totals top-spaced">
                    <td>
                        <strong>{t('components.totalDropdown.totalNetValue')}</strong>
                    </td>
                    <td className="a-right">
                        <strong>
                            {t('numberFormat.numberWithCurrencyName', {
                                val: FormatCurrency.convertIntegerToCurrencyFormat(totalNetValue),
                                currencyName: currency
                            })}
                        </strong>
                    </td>
                </tr>
                <tr className="totals last">
                    <td>
                        <strong>{t('components.totalDropdown.totalTax', { tax: spoc?.taxName ?? 'tax' })}</strong>
                    </td>
                    <td className="a-right">
                        <strong>
                            {t('numberFormat.numberWithCurrencyName', {
                                val: FormatCurrency.convertIntegerToCurrencyFormat(totalTaxValue),
                                currencyName: currency
                            })}
                        </strong>
                    </td>
                </tr>
                <tr className="footer">
                    <td>
                        <strong>
                            {textTotalLine}&nbsp;
                            <span className="small">
                                {' '}
                                {t('components.totalDropdown.incTax', { tax: spoc?.taxName ?? 'tax' })}
                            </span>
                        </strong>
                    </td>
                    <td className="a-right">
                        <strong>
                            {t('numberFormat.numberWithCurrencyName', {
                                val: FormatCurrency.convertIntegerToCurrencyFormat(totalValue),
                                currencyName: currency
                            })}
                        </strong>
                    </td>
                </tr>
            </tbody>
        </table>
    );
};
